import type { AgentTicketOptions, TicketDetail } from "@noverik/contracts";
import {
  ArrowRightLeft,
  CheckCircle2,
  MessageSquareReply,
  NotebookPen,
  Paperclip,
  UserCheck,
  UsersRound,
  X,
} from "lucide-react";
import { type FormEvent, useState } from "react";
import { useAuth } from "../auth/AuthContext";
import { apiRequest, ApiRequestError } from "../lib/api";
import { encodeAttachments } from "../lib/files";
import { RichTextEditor, hasRichTextContent } from "./RichTextEditor";

type Props = {
  ticket: TicketDetail;
  options: AgentTicketOptions;
  onUpdated: (ticket: TicketDetail, options?: AgentTicketOptions) => void;
  reloadOptions: () => Promise<AgentTicketOptions>;
};

export function AgentTicketActions({
  ticket,
  options,
  onUpdated,
  reloadOptions,
}: Props) {
  const { session } = useAuth();
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [statusId, setStatusId] = useState(String(options.currentStatusId));
  const [assignee, setAssignee] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const csrf = session?.csrfToken;

  const refreshOptions = async (updated: TicketDetail) => {
    const next = await reloadOptions();
    setStatusId(String(next.currentStatusId));
    setAssignee("");
    setDepartmentId("");
    onUpdated(updated, next);
  };

  const request = async (
    suffix: string,
    body: Record<string, unknown>,
    failure: string,
  ) => {
    if (!csrf) {
      setError("Sua sessão expirou. Atualize a página.");
      return;
    }

    setBusy(true);
    setError(null);

    try {
      const updated = await apiRequest<TicketDetail>(
        "/tickets/" + encodeURIComponent(ticket.number) + "/agent/" + suffix,
        {
          method: "POST",
          csrfToken: csrf,
          body,
        },
      );
      await refreshOptions(updated);
    } catch (err) {
      setError(err instanceof ApiRequestError ? err.message : failure);
      throw err;
    } finally {
      setBusy(false);
    }
  };

  const publish = async (event: FormEvent) => {
    event.preventDefault();

    setBusy(true);
    setError(null);

    try {
      if (!csrf) {
        throw new Error("Sua sessão expirou. Atualize a página.");
      }

      if (!hasRichTextContent(message)) {
        throw new Error("Digite uma mensagem antes de publicar.");
      }

      const attachments = await encodeAttachments(files);
      const endpoint = mode === "reply" ? "replies" : "notes";
      const updated = await apiRequest<TicketDetail>(
        "/tickets/" + encodeURIComponent(ticket.number) + "/agent/" + endpoint,
        {
          method: "POST",
          csrfToken: csrf,
          body: {
            message,
            attachments,
            replyTo: "all",
            signature: "none",
          },
        },
      );

      setMessage("");
      setFiles([]);
      await refreshOptions(updated);
    } catch (err) {
      setError(
        err instanceof ApiRequestError || err instanceof Error
          ? err.message
          : "Não foi possível concluir a ação.",
      );
    } finally {
      setBusy(false);
    }
  };

  const changeStatus = async () => {
    const nextId = Number(statusId);
    if (!Number.isInteger(nextId) || nextId === options.currentStatusId) return;

    try {
      await request(
        "status",
        { statusId: nextId },
        "Não foi possível alterar o status.",
      );
    } catch {
      setStatusId(String(options.currentStatusId));
    }
  };

  const claim = async () => {
    try {
      await request("claim", {}, "Não foi possível assumir o chamado.");
    } catch {
      // A mensagem já foi exibida.
    }
  };

  const assign = async () => {
    if (!assignee) return;

    try {
      await request(
        "assign",
        { target: assignee },
        "Não foi possível atribuir o chamado.",
      );
    } catch {
      // A mensagem já foi exibida.
    }
  };

  const transfer = async () => {
    const nextDepartmentId = Number(departmentId);
    if (!Number.isInteger(nextDepartmentId) || nextDepartmentId <= 0) return;

    try {
      await request(
        "transfer",
        { departmentId: nextDepartmentId },
        "Não foi possível transferir o chamado.",
      );
    } catch {
      // A mensagem já foi exibida.
    }
  };

  return (
    <section className="agent-actions">
      <div className="agent-action-toolbar">
        <div className="agent-status-control">
          <CheckCircle2 size={17} />
          <select
            value={statusId}
            disabled={busy}
            onChange={(event) => setStatusId(event.target.value)}
          >
            {options.statuses.map((status) => (
              <option key={status.id} value={status.id}>
                {status.label}{status.current ? " (atual)" : ""}
              </option>
            ))}
          </select>
          <button
            className="button secondary"
            type="button"
            disabled={busy || Number(statusId) === options.currentStatusId}
            onClick={() => void changeStatus()}
          >
            Alterar status
          </button>
        </div>

        {!options.assigned && options.permissions.assign && (
          <button
            className="button secondary"
            type="button"
            disabled={busy}
            onClick={() => void claim()}
          >
            <UserCheck size={16} /> Assumir chamado
          </button>
        )}
      </div>

      {(options.permissions.assign || options.permissions.transfer) && (
        <div className="agent-routing">
          {options.permissions.assign && options.assignees.length > 0 && (
            <div className="routing-control">
              <UsersRound size={16} />
              <select
                value={assignee}
                disabled={busy}
                onChange={(event) => setAssignee(event.target.value)}
              >
                <option value="">Atribuir para agente ou equipe</option>
                {options.assignees.map((target) => (
                  <option key={target.id} value={target.id}>
                    {target.type === "team" ? "Equipe: " : ""}{target.name}
                  </option>
                ))}
              </select>
              <button
                className="button secondary"
                type="button"
                disabled={busy || !assignee}
                onClick={() => void assign()}
              >
                Atribuir
              </button>
            </div>
          )}

          {options.permissions.transfer && options.departments.length > 0 && (
            <div className="routing-control">
              <ArrowRightLeft size={16} />
              <select
                value={departmentId}
                disabled={busy}
                onChange={(event) => setDepartmentId(event.target.value)}
              >
                <option value="">Transferir para departamento</option>
                {options.departments.map((department) => (
                  <option key={department.id} value={department.id}>
                    {department.name}
                  </option>
                ))}
              </select>
              <button
                className="button secondary"
                type="button"
                disabled={busy || !departmentId}
                onClick={() => void transfer()}
              >
                Transferir
              </button>
            </div>
          )}
        </div>
      )}

      {(options.permissions.reply || options.permissions.note) && (
        <form className="agent-composer" onSubmit={publish}>
          <div className="composer-tabs">
            {options.permissions.reply && (
              <button
                type="button"
                className={mode === "reply" ? "active" : ""}
                onClick={() => setMode("reply")}
              >
                <MessageSquareReply size={16} /> Responder cliente
              </button>
            )}
            {options.permissions.note && (
              <button
                type="button"
                className={mode === "note" ? "active note" : ""}
                onClick={() => setMode("note")}
              >
                <NotebookPen size={16} /> Nota interna
              </button>
            )}
          </div>

          <RichTextEditor
            value={message}
            onChange={setMessage}
            disabled={busy}
            minHeight={165}
            label={mode === "reply" ? "Resposta ao cliente" : "Nota interna"}
            placeholder={
              mode === "reply"
                ? "Escreva a resposta que será enviada ao cliente..."
                : "Registre uma informação interna para a equipe..."
            }
          />

          <div className="composer-footer">
            <div className="reply-files">
              <label className="button secondary file-button">
                <Paperclip size={16} />
                Anexar
                <input
                  type="file"
                  multiple
                  onChange={(event) => {
                    const selected = Array.from(event.target.files ?? []);
                    setFiles((current) => [...current, ...selected].slice(0, 10));
                    event.target.value = "";
                  }}
                />
              </label>

              {files.map((file, index) => (
                <span className="reply-file" key={file.name + file.lastModified + index}>
                  {file.name}
                  <button
                    type="button"
                    onClick={() => setFiles((current) => current.filter((_, position) => position !== index))}
                    aria-label={"Remover " + file.name}
                  >
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>

            <button className={mode === "note" ? "button warning" : "button primary"} disabled={busy}>
              {busy ? "Processando..." : mode === "note" ? "Publicar nota interna" : "Enviar resposta"}
            </button>
          </div>

          {options.lockRequired && (
            <small className="lock-hint">
              O bloqueio exclusivo do osTicket é adquirido automaticamente durante a publicação.
            </small>
          )}

          {error && <div className="login-message form-error">{error}</div>}
        </form>
      )}
    </section>
  );
}
